import * as Sentry from "@sentry/react";
import { getEnvironment } from "./environment";
if (!Sentry.isInitialized()) {
    init();
}
export const captureException = Sentry.captureException;
const EXTRA_KEY = "ROUTE_TO";
function isPartialMetaData(value) {
    return (typeof value === "object" && value !== null && "dsn" in value && typeof value.dsn === "string");
}
function toModuleMetadata(partial) {
    const { name, dsn, release } = partial;
    if (release) {
        return {
            dsn,
            release,
        };
    }
    if (name) {
        const release = window.__LR__.dependencies.use(name).version.id;
        return { dsn, release };
    }
    return {
        dsn,
        release: "unknown",
    };
}
function resolveModuleMetadata(value) {
    if (!Array.isArray(value)) {
        return null;
    }
    const metadata = value.filter(isPartialMetaData).map(toModuleMetadata);
    if (metadata.length === 0) {
        return null;
    }
    return metadata;
}
function resolveModuleMetadataFromEvent(event) {
    let metadata = resolveModuleMetadata(event?.extra?.[EXTRA_KEY]);
    metadata ?? (metadata = resolveModuleMetadata([event?.contexts?.microfrontend]));
    metadata ?? (metadata = []);
    return metadata;
}
/**
 * This integration is used in cases where a stack trace may be necessary to
 * determine the correct project to send the event to, but the error is created
 * by shared code that is not in the same project as the error.
 *
 * For example, a GraphQL network error is created by Apollo Client, and not triggered
 * by the app code, but we still want the app that made the request to have the error assigned to it.
 *
 * Uses a similar algorithm to the one used in the `moduleMetadataIntegration`.
 * See: https://github.com/getsentry/sentry-javascript/blob/b4bd21fde0054285d6123e0c4ec351a28885e9b3/packages/core/src/metadata.ts#L11
 */
function indirectStackContextIntegration() {
    const filenameMetadataMap = new Map();
    const parsedStacks = new Set();
    function parseGlobalModuleMetadata(parser) {
        const globalModuleMetadata = window._sentryModuleMetadata;
        if (!globalModuleMetadata) {
            return;
        }
        for (const [stack, metadata] of Object.entries(globalModuleMetadata)) {
            if (parsedStacks.has(stack)) {
                continue;
            }
            parsedStacks.add(stack);
            const frames = parser(stack);
            for (const frame of frames.reverse()) {
                if (frame.filename) {
                    filenameMetadataMap.set(frame.filename, metadata);
                    break;
                }
            }
        }
    }
    function findMetadataForIndirectStack(parser, indirectStack) {
        const frames = parser(indirectStack);
        for (const frame of frames.reverse()) {
            if (frame.filename) {
                const metadata = filenameMetadataMap.get(frame.filename);
                if (metadata) {
                    return metadata;
                }
            }
        }
        return null;
    }
    return {
        name: "LobsterRollIndirectStackContextIntegration",
        processEvent(event, hint, client) {
            const indirectStack = event.contexts?.LOBSTER_ROLL_SENTRY_INDIRECT_STACK;
            if (typeof indirectStack?.stack !== "string") {
                return event;
            }
            const { stackParser } = client.getOptions();
            parseGlobalModuleMetadata(stackParser);
            const metadata = findMetadataForIndirectStack(stackParser, indirectStack.stack);
            if (metadata) {
                event.extra = {
                    ...event.extra,
                    [EXTRA_KEY]: [metadata],
                    LOBSTER_ROLL_SENTRY_INDIRECT_STACK: indirectStack,
                };
            }
            return event;
        },
    };
}
/**
 * See: https://docs.sentry.io/platforms/javascript/best-practices/micro-frontends/
 *
 * This is necessary to be able to attribute errors to the correct project as
 * Lobster Roll is a micro-frontend framework.
 */
function attachRouteToFromModuleMetadataIntegration() {
    return {
        name: "LobsterRollAttachRouteToFromModuleMetadataIntegration",
        processEvent(event) {
            const frames = event?.exception?.values?.[0].stacktrace?.frames;
            if (!frames) {
                return event;
            }
            const base = [];
            // Find the all of the frames that have a `module_metadata` key.
            const routeTo = frames
                .filter((frame) => frame.module_metadata && frame.module_metadata.dsn)
                .map((frame) => frame.module_metadata)
                .reduce((acc, frame) => (acc.find((prev) => prev.dsn === frame.dsn) ? acc : acc.concat(frame)), base);
            if (routeTo.length) {
                event.extra = {
                    ...event.extra,
                    [EXTRA_KEY]: routeTo,
                };
            }
            return event;
        },
    };
}
function attachLobsterRollCacheIntegration() {
    return {
        name: "AttachLobsterRollCacheIntegration",
        async processEvent(event) {
            const cache = window.__LR__ ? await window.__LR__.dependencies.getCache() : {};
            event.extra = {
                ...event.extra,
                lobsterRoll: cache,
            };
            event.tags ?? (event.tags = {});
            for (const [key, value] of Object.entries(cache)) {
                event.tags[`${key}-version`] = value.version.id;
            }
            return event;
        },
    };
}
function init() {
    const environment = getEnvironment(window.location.href);
    const transport = Sentry.makeMultiplexedTransport(Sentry.makeFetchTransport, (args) => {
        /**
         * Make sure all events are passed through to the correct project.
         */
        const event = args.getEvent([
            "client_report",
            "user_report",
            "feedback",
            "session",
            "sessions",
            "transaction",
            "attachment",
            "event",
            "profile",
            "profile_chunk",
            "replay_event",
            "replay_recording",
            "check_in",
            "statsd",
            "span",
        ]);
        return resolveModuleMetadataFromEvent(event);
    });
    Sentry.init({
        environment,
        /**
         * The DSN for the "Federation" project in Sentry.
         * https://do-observability.sentry.io/projects/federation/?project=4507504849256448
         */
        dsn: "https://aee88abdb1378a68fd5ff728abdf2694@o1109393.ingest.us.sentry.io/4507504849256448",
        integrations: [
            Sentry.moduleMetadataIntegration(),
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
            attachLobsterRollCacheIntegration(),
            attachRouteToFromModuleMetadataIntegration(),
            indirectStackContextIntegration(),
        ],
        transport,
        beforeSend(event, hint) {
            const metadata = resolveModuleMetadataFromEvent(event);
            const defaultSampleRate = process.env.NODE_ENV === "production" ? 0.1 : 1;
            /**
             * Override sample rates for specific modules.
             */
            const sampleRateOverrides = {
                // ui-gen-ai
                "https://29bad840f3805e2a89cff3f81e20b0d7@o1109393.ingest.us.sentry.io/4507578809909248": 1,
            };
            /**
             * Calculate the sample rate of all module metadatas.
             */
            const sampleRates = metadata.map((meta) => sampleRateOverrides[meta.dsn] ?? defaultSampleRate);
            /**
             * Take the maximum sample rate as the effective sample rate.
             */
            const sampleRate = Math.max(defaultSampleRate, ...sampleRates);
            if (Math.random() <= sampleRate) {
                return event;
            }
            return null;
        },
        /**
         * This is set to 1 so that we always invoke the `beforeSend` function.
         */
        sampleRate: 1,
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.01,
        replaysOnErrorSampleRate: 0.01,
        allowUrls: [
            /https?:\/\/(([a-zA-Z0-9][a-zA-Z0-9.-]+)\.)?digitalocean\.com/,
            /https?:\/\/(([a-zA-Z0-9][a-zA-Z0-9.-]+)\.)?digitalocean-ui\.com/,
        ],
        denyUrls: [
            /\.amplitude\.com/,
            /segment\.digitalocean\.com/,
            /\.fullstory\.com/,
            /\.googletagmanager\.com/,
            /\.google-analytics\.com/,
            /\.pendo\.io/,
            /\.schemaapp\.com/,
            /\.segment\.com/,
        ],
        ignoreErrors: [
            /^Non-Error promise rejection captured with value:/,
            "Error: AbortError: The operation was aborted.",
            "Error: AbortError: The user aborted a request.",
            "ReferenceError: Can't find variable: twttr",
            "ReferenceError: Can't find variable: instantSearchSDKJSBridgeClearHighlight'",
            "ReferenceError: Can't find variable: QK_middlewareReadModePageDetect'",
            "ReferenceError: Can't find variable: WeixinJSBridge'",
            "ReferenceError: playBackgroundMusic is not defined",
            "ReferenceError: $ is not defined",
            "ReferenceError: jQuery is not defined",
            "ReferenceError: privateSpecialRepair is not defined",
            "ReferenceError: UET is not defined",
            "TypeError: Cannot read property 'getReadModeExtract' of undefined",
            "TypeError: Cannot read property 'getReadModeConfig' of undefined",
            "TypeError: Cannot read property 'getReadModeRender' of undefined",
            "TypeError: null is not an object (evaluating 'document.head.querySelector(\"meta[name='supported-color-scheme...",
            "TypeError: window.bannerNight is not a function",
            "TypeError: Cannot read properties of null (reading 'disconnect')",
            "TypeError: Cannot redefine property: BetterJsPop",
            "TypeError: undefined is not an object (evaluating 'ceCurrentVideo.currentTime')",
            "ResizeObserver loop limit exceeded",
            "ResizeObserver loop completed with undelivered notifications",
            /ChunkLoadError: Loading chunk/,
            /Blocked a frame with origin/,
            "Non-Error promise rejection captured with keys",
            "Non-Error promise rejection captured",
            "Non-Error exception captured",
            "`CustomEvent` (type=unhandledrejection) captured as promise rejection",
        ],
    });
    window.Sentry = Sentry;
}
